import _ from 'lodash';
import { useState, useEffect, createContext, useContext } from 'react';
import { useNavigate } from 'react-router';
import http from 'httpClient';
import { WebAuth, Auth0DecodedHash, Auth0ParseHashError } from 'auth0-js';
import { createDate } from 'utils/dateAndTimeUtils';
import { clear } from './clear';
import { AxiosResponse } from 'axios';
import { AuthenticatedUserInfoRpt } from 'types';
import { AuthenticationStateType } from './types';
import {
  AUTH0_RESPONSE_TYPE,
  AUTH0_DOMAIN,
  AUTH0_CLIENT_ID,
  AUTH0_REDIRECT_URL,
  AUTH0_AUDIENCE,
  AUTH0_SCOPE,
} from 'config';

interface AuthenticationProviderProps {
  children: React.ReactNode;
}

const AuthenticationContext = createContext<AuthenticationStateType | undefined>(undefined);

export const AuthenticationProvider = ({ children }: AuthenticationProviderProps) => {
  const [error, setError] = useState<Auth0ParseHashError | false>(false);
  const navigate = useNavigate();
  const auth = new WebAuth({
    responseType: AUTH0_RESPONSE_TYPE,
    domain: AUTH0_DOMAIN,
    clientID: AUTH0_CLIENT_ID,
    redirectUri: AUTH0_REDIRECT_URL,
    audience: AUTH0_AUDIENCE,
    scope: AUTH0_SCOPE,
  });

  const ACCESS_TOKEN = 'CLEOSCLIENT_accesstoken';
  const TOKEN_ID = 'CLEOSCLIENT_tokenid';
  const EXPIRES_AT = 'CLEOSCLIENT_expiresat';
  const PERMISSIONS = 'CLEOSCLIENT_permissions';
  const USERNAME = 'CLEOSCLIENT_username';
  const NAME = 'CLEOSCLIENT_name';
  const PREVIOUS_SESSION = 'CLEOSCLIENT_prevsession';
  const ENTURUSER = 'CLEOSCLIENT_enturUser';

  const saveAuthResult = (parsedHash: Auth0DecodedHash) => {
    const expiresAt = parsedHash.expiresIn
      ? JSON.stringify(parsedHash.expiresIn * 1000 + createDate().getTime())
      : null;
    try {
      localStorage.setItem(ACCESS_TOKEN, parsedHash.accessToken ?? '');
      localStorage.setItem(TOKEN_ID, parsedHash.idToken ?? '');
      localStorage.setItem(EXPIRES_AT, expiresAt ?? '');
    } catch (e) {
      console.error(e);
    }
  };

  const saveUser = (user: AuthenticatedUserInfoRpt, permissions: string) => {
    try {
      localStorage.setItem(USERNAME, user.username || '');
      localStorage.setItem(NAME, user.name ?? '');
      localStorage.setItem(PERMISSIONS, permissions);
      localStorage.setItem(ENTURUSER, user.enturUser ? 'true' : 'false');
    } catch (e) {
      console.error(e);
    }
  };

  const saveSession = (path: string) => {
    try {
      sessionStorage.setItem(PREVIOUS_SESSION, path);
    } catch (e) {
      console.error(e);
    }
  };
  const hasPrivilege = (privileges: string | string[]) => {
    const perm = localStorage.getItem(PERMISSIONS);
    const userPermissions = perm ? perm.split(',') : [];
    const requiredPrivileges = typeof privileges === 'string' ? [privileges] : [...privileges];
    return _.difference(requiredPrivileges, userPermissions).length === 0;
  };

  const getItem = (key: string) => {
    let item;
    try {
      item = localStorage.getItem(key);
    } catch (e) {
      console.error(e);
    }
    return item;
  };

  const getToken = () => {
    let token;
    try {
      token = localStorage.getItem(ACCESS_TOKEN);
    } catch (e) {
      console.error(e);
    }
    return token;
  };

  const hasTokenExpired = () => {
    let hasExpired = false;
    try {
      hasExpired = createDate().getTime() > parseInt(getItem(EXPIRES_AT) || '0', 10);
    } catch (e) {
      console.error(e);
    }
    return hasExpired;
  };

  const isAuthenticated = () => !(!getToken() || hasTokenExpired());

  const getBearer = () => {
    let bearer = '';
    try {
      bearer = `Bearer ${getToken() || ''}`;
    } catch (e) {
      console.error(e);
    }
    return bearer;
  };

  const getPreviousSession = () => {
    let previousSession;
    try {
      previousSession = sessionStorage.getItem(PREVIOUS_SESSION);
    } catch (e) {
      console.error(e);
    }
    return previousSession;
  };

  const getUser = () => {
    let user;
    try {
      user = {
        username: localStorage.getItem(USERNAME) || '',
        name: localStorage.getItem(NAME) || 'Ukjent bruker',
        permissions: localStorage.getItem(PERMISSIONS) || '',
        enturUser: localStorage.getItem(ENTURUSER) === 'true',
      };
    } catch (e) {
      console.error(e);
    }
    return user;
  };

  const clearPreviousSession = () => {
    try {
      sessionStorage.removeItem(PREVIOUS_SESSION);
    } catch (e) {
      console.error(e);
    }
  };

  const fetchPermissions = async () => {
    http.get('/permissions/user/me').then((res: AxiosResponse<AuthenticatedUserInfoRpt>) => {
      const user = res.data;
      const { permissions } = user;
      saveUser(user, permissions?.map((permission) => `${permission.operation}.${permission.access}`).toString() || '');
      const previousSession = getPreviousSession();
      clearPreviousSession();
      navigate(previousSession || '/', { replace: true });
    });
  };

  const handleAuthentication = async () => {
    auth.parseHash({ hash: window.location.hash }, (err, authResult) => {
      if (authResult && authResult.accessToken && authResult.idToken) {
        saveAuthResult(authResult);
        localStorage.setItem('bearer', `Bearer ${getToken() || ''}`);
        fetchPermissions();
      } else if (err) {
        setError(err);
      }
    });
  };

  const logIn = () => {
    auth.authorize();
  };

  const logOut = () => {
    clear();
    clearPreviousSession();
    auth.logout({
      clientID: AUTH0_CLIENT_ID,
      returnTo: `${window.location.origin}/login`,
    });
  };

  const checkIfTokenHasExpired = () => {
    setTimeout(() => {
      if (hasTokenExpired()) {
        if (window.location.pathname === '/oops' || window.location.pathname === '/login') return;
        navigate('/oops', {
          replace: true,
          state: { referrer: `${window.location.pathname}${window.location.search}`, restoreCrumbs: true },
        });
      } else {
        checkIfTokenHasExpired();
      }
    }, 10000);
  };

  const internal = getItem(ENTURUSER) === 'true';

  useEffect(() => {
    checkIfTokenHasExpired();
  }, []); //eslint-disable-line

  return (
    <AuthenticationContext.Provider
      value={{
        logIn,
        logOut,
        isAuthenticated,
        getToken,
        handleAuthentication,
        getItem,
        hasPrivilege,
        clear,
        clearPreviousSession,
        hasTokenExpired,
        getUser,
        getPreviousSession,
        getBearer,
        saveSession,
        saveUser,
        saveAuthResult,
        error,
        internal,
      }}
    >
      {children}
    </AuthenticationContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthenticationContext);
  if (!context) {
    throw new Error('useAuth must be used within a AuthenticationProvider');
  }
  return context;
};
